import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormField } from '../../../../../../../sellions_forms/projects/forms/src/lib/interfaces/form-field.interface';
import { FormVariable, FormVariables } from '../../../../../../../sellions_forms/projects/forms/src/lib/interfaces/form-variables.interface';
import { SellionsFormsRendererComponent } from '../../../../../../../sellions_forms/projects/forms/src/lib/components/renderer/renderer.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { MultipartFile } from '../../../../../../../sellions_forms/projects/forms/src/lib/components/controls/multipart-files/multipart-file';
import { CloseDialogComponent } from './close-dialog/close-dialog.component';
import { Subject } from 'rxjs';
import { FormDataService } from '../../../../../../../sellions_forms/projects/forms/src/lib/services/form-data/form-data.service';

export interface FormViewerComponentData {
  form: FormField[];
  formName: string;
  formError: Subject<string>;
  dataViewBaseAPI: string;
  sendButtonText: string;
  answers: any;
}

@Component({
  selector: 'form-viewer',
  templateUrl: './form-viewer.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class FormViewerComponent implements OnInit, OnDestroy {
  @Input() form: FormField[];
  @Input() formName: string;
  @Input() sendButtonText: string;
  @Input() formError: Subject<string>;
  @Input() dataViewBaseAPI: string;
  @Input() answers: any;

  @ViewChild(SellionsFormsRendererComponent) sellionsFormsRendererComponent: SellionsFormsRendererComponent;
  @ViewChild('content') formViewerContent: ElementRef;

  @Output() processVariables: EventEmitter<FormVariables> = new EventEmitter<FormVariables>();

  private formData: { [key: string]: any } = {};
  public isSendButtonDisabled = false;

  constructor(
    private formDataService: FormDataService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: FormViewerComponentData,
  ) {}

  ngOnInit() {
    if (this.data) {
      this.form = this.data.form;
      this.formName = this.data.formName;
      this.formError = this.data.formError;
      this.dataViewBaseAPI = this.data.dataViewBaseAPI;
      this.sendButtonText = this.data.sendButtonText || 'Zapisz';
      if (this.data.answers) {
        this.answers = this.getAnswers(this.data.answers);
      }
    }
  }

  public send() {
    if (!this.isFormValid() || this.isSendButtonDisabled) {
      this.formViewerContent.nativeElement.scrollTo(0, 0);
      this.formError.next('Formularz wypełniony niepoprawnie');
      return;
    }

    this.isSendButtonDisabled = true;

    const result: FormVariables = this.formDataService.mapFormRawDataToFormVariables(this.formData, this.form);

    this.processVariables.emit(result);
  }

  private handleFormData(formDataWithReferences: { [key: string]: object }, form: FormField[], resultFiles: MultipartFile[]): FormVariable[] {
    const variables: FormVariable[] = [];

    for (const key in formDataWithReferences) {
      const field = this.getFormField(key, form);
      const value = formDataWithReferences[key];

      if (field.type === 'multipartFiles') {
        variables.push(this.handleMultipartFiles(key, value as MultipartFile[], resultFiles));
      } else if (field.type === 'list') {
        variables.push(this.handleListField(field, value as any[], resultFiles));
      } else {
        variables.push({ name: key, value });
      }
    }

    return variables;
  }

  private getFormField(key: string, form: FormField[]): FormField {
    const field = form.find((field) => field.id === key);
    if (!field) {
      throw new Error(`Cannot find field with id: '${key}'`);
    }
    return field;
  }

  private handleListField(field: FormField, arrayValue: any[], resultMultipartFiles: MultipartFile[]): FormVariable {
    if (arrayValue == null) {
      return { name: field.id, value: null };
    }

    const subFormList = [];
    for (const element of arrayValue) {
      subFormList.push(this.handleFormData(element as {}, field.subForm.definition, resultMultipartFiles));
    }
    return { name: field.id, value: subFormList };
  }

  private handleMultipartFiles(variableName: string, multipartFiles: MultipartFile[], resultMultipartFiles: MultipartFile[]): FormVariable {
    if (multipartFiles == null) {
      return { name: variableName, value: null };
    }

    const fileNameArray: string[] = [];

    multipartFiles.forEach((part) => {
      if (MultipartFile.isMultipartFile(part)) {
        resultMultipartFiles.push(part);
        fileNameArray.push(part.fileNameWithPath);
      } else {
        throw new Error('The array contains a type other than File');
      }
    });

    return { name: variableName, value: fileNameArray };
  }

  public formChangeUpdate(data: FormGroup) {
    this.formData = data.getRawValue();
  }

  public isFormValid(): boolean {
    return this.sellionsFormsRendererComponent.isFormValid();
  }

  public confirmClosing() {
    const hasChanges = this.sellionsFormsRendererComponent.hasChanges();
    if (hasChanges) {
      const dialogRef = this.dialog.open(CloseDialogComponent, { panelClass: 'form-viewer-close-dialog', disableClose: true });
      dialogRef.componentInstance.onDialogClose.subscribe((shouldClose) => {
        if(shouldClose) {
          this.processVariables.complete();
        }
      })
    } else {
      this.processVariables.complete();
      this.dialog.closeAll();
    }
  }

  getAnswers(data: any) {
    const answers = {};
    this.form.forEach((formControl) => {
      const paramName = formControl.id;
      if (data[paramName]) {
        answers[paramName] = data[paramName];
      }
    });
    return answers;
  }

  clearError(): void {
    this.formError.next('');
  }

  ngOnDestroy(): void {
    this.clearError();
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/components/form-viewer/form-viewer.component.ts